import type { BrandOverrides } from '@alison/whitelabel';
import type { Shadows, Theme } from '@mui/material/styles';
import { alpha, createTheme } from '@mui/material/styles';
import { createElement } from 'react';
import './augmentation';
import {
  attribute,
  category,
  chartSeries,
  elementCategory,
  flightStage,
  gradientText,
  layout,
  mergeBrand,
  network,
  radius,
  SPACING_UNIT,
  score,
  shadows,
  state,
  white,
} from './tokens';

/** Builds the app theme from brand tokens; a tenant passes overrides, Alison passes none.
 * Component overrides read `b`, never the raw Alison tokens, so a palette override reaches them. */
export function createAppTheme(overrides?: BrandOverrides): Theme {
  const b = mergeBrand(overrides);
  const buttonPrimaryShadow = `0px 4px 12px 0px ${alpha(b.primary.main, 0.14)}`;
  const muiShadows: Shadows = [
    'none',
    shadows.chrome,
    shadows.chrome,
    shadows.chrome,
    buttonPrimaryShadow,
    shadows.card,
    shadows.card,
    shadows.tooltip,
    ...(Array(17).fill(shadows.card) as string[]),
  ] as Shadows;

  return createTheme({
    spacing: SPACING_UNIT,
    shape: { borderRadius: radius.lg },
    shadows: muiShadows,

    gradients: b.gradients,
    layout,
    radius,
    mixins: { gradientText: { ...gradientText, background: b.gradients.primary } },

    palette: {
      mode: 'light',
      common: { black: '#000000', white: white },
      primary: {
        main: b.primary.main,
        dark: b.primary.dark,
        light: b.primary.light,
        bg: b.primary.bg,
        contrastText: white,
      },
      secondary: {
        main: b.secondary.main,
        dark: b.secondary.dark,
        light: b.secondary.light,
        contrastText: white,
      },
      success: {
        main: state.success.main,
        dark: state.success.dark,
        bg: state.success.bg,
        contrastText: white,
      },
      warning: {
        main: state.warning.main,
        dark: state.warning.dark,
        bg: state.warning.bg,
        contrastText: white,
      },
      error: {
        main: state.error.main,
        dark: state.error.dark,
        bg: state.error.bg,
        contrastText: white,
      },
      info: {
        main: b.info.main,
        bg: b.info.bg,
        contrastText: white,
      },
      text: {
        primary: b.text.primary,
        secondary: b.text.secondary,
        disabled: b.text.disabled,
        muted: b.text.muted,
      },
      action: {
        active: b.text.secondary,
        hover: alpha(b.primary.main, 0.04),
        selected: alpha(b.primary.main, 0.08),
        disabled: b.text.disabled,
        disabledBackground: b.background.muted,
      },
      background: {
        default: b.background.page,
        paper: b.background.paper,
        page: b.background.page,
        platform: b.background.platform,
        muted: b.background.muted,
        icon: b.background.icon,
      },
      divider: b.border.default,
      border: b.border,
      network,
      category,
      score,
      attribute,
      flightStage,
      elementCategory,
      chartSeries,
    },

    typography: {
      fontFamily: b.fontFamily,
      htmlFontSize: 16,
      fontSize: 12,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: { fontSize: '1.5rem', fontWeight: 500, lineHeight: 1.25 },
      h2: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.25 },
      h3: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.25 },
      h4: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.25 },
      h5: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.25 },
      h6: { fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.25 },
      subtitle1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.5 },
      subtitle2: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.5 },
      body1: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.5 },
      body2: { fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.5 },
      button: {
        fontSize: '0.75rem',
        fontWeight: 800,
        lineHeight: 1.25,
        textTransform: 'none',
      },
      caption: { fontSize: '0.688rem', fontWeight: 600, lineHeight: 1.4 },
      overline: {
        fontSize: '0.625rem',
        fontWeight: 600,
        lineHeight: 1.4,
        textTransform: 'uppercase',
      },
      nav: {
        fontSize: '0.55rem',
        fontWeight: 600,
        lineHeight: 1.2,
        textTransform: 'uppercase',
      },
    },

    components: {
      MuiAlert: {
        defaultProps: {
          iconMapping: {
            error: createElement('img', {
              src: new URL('../assets/error-warning.svg', import.meta.url).href,
              alt: '',
              width: 16,
              height: 16,
            }),
          },
        },
        styleOverrides: {
          root: ({ ownerState }) =>
            ownerState.severity === 'error'
              ? {
                  backgroundColor: alpha(state.error.dark, 0.08),
                  border: `1px solid ${alpha(state.error.dark, 0.25)}`,
                  borderRadius: radius.md,
                  padding: '14px 16px',
                  gap: 10,
                  alignItems: 'flex-start',
                  color: '#6B2933',
                  fontSize: 12,
                  lineHeight: 1.4,
                  '& .MuiAlert-icon': { padding: 0, margin: 0, opacity: 1 },
                  '& .MuiAlert-message': {
                    padding: 0,
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                    fontSize: 13,
                    fontWeight: 600,
                    color: state.error.dark,
                    '&:has(.MuiAlertTitle-root)': {
                      fontSize: 12,
                      fontWeight: 400,
                      color: '#6B2933',
                    },
                  },
                  '& .MuiAlertTitle-root': {
                    margin: '0 0 4px',
                    color: state.error.dark,
                    fontSize: 13,
                    fontWeight: 600,
                    lineHeight: 'normal',
                  },
                  '& .MuiLink-root': {
                    color: state.error.dark,
                    fontSize: 12,
                    fontWeight: 700,
                    textDecoration: 'underline',
                    textUnderlinePosition: 'from-font',
                  },
                }
              : {},
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontSize: '0.75rem',
            color: b.text.primary,
            backgroundColor: b.background.paper,
          },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            textTransform: 'none',
            fontWeight: 800,
            boxShadow: 'none',
          },
          sizeSmall: { padding: '4px 12px', fontSize: '0.688rem' },
          sizeMedium: { padding: '8px 16px' },
          sizeLarge: { padding: '12px 24px', fontSize: '0.813rem' },
        },
        variants: [
          {
            props: { variant: 'gradient' },
            style: {
              color: white,
              background: b.gradients.primary,
              boxShadow: buttonPrimaryShadow,
              '&:hover': { background: b.gradients.primaryHover },
              '&.Mui-disabled': { color: alpha('#FFFFFF', 0.7), opacity: 0.6 },
            },
          },
          {
            props: { variant: 'unlock' },
            style: {
              color: white,
              borderRadius: radius.pill,
              background: b.gradients.unlock,
              boxShadow: shadows.card,
              '&:hover': { filter: 'brightness(0.96)' },
            },
          },
          {
            props: { variant: 'soft' },
            style: {
              color: b.primary.main,
              backgroundColor: b.primary.light,
              '&:hover': { backgroundColor: alpha(b.primary.main, 0.12) },
            },
          },
          {
            props: { variant: 'outlined' },
            style: {
              borderColor: b.border.strong,
              color: b.text.primary,
              '&:hover': {
                borderColor: b.border.primary,
                backgroundColor: b.background.page,
              },
            },
          },
          {
            props: { variant: 'text' },
            style: {
              color: b.text.primary,
              '&:hover': { backgroundColor: alpha(b.primary.main, 0.05) },
            },
          },
        ],
      },

      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none', borderRadius: radius.lg },
          outlined: { borderColor: b.border.default },
        },
      },

      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: radius.xxl,
            border: `1px solid ${b.border.default}`,
            boxShadow: shadows.card,
            backgroundImage: 'none',
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, fontSize: '0.688rem' },
          sizeSmall: { height: 20 },
        },
        variants: [
          {
            props: { variant: 'gradient' },
            style: {
              color: white,
              background: b.gradients.primary,
              border: 'none',
            },
          },
          {
            props: { variant: 'soft' },
            style: {
              color: b.primary.main,
              backgroundColor: b.primary.light,
              border: 'none',
            },
          },
        ],
      },

      MuiLinearProgress: {
        styleOverrides: {
          root: {
            height: 8,
            borderRadius: radius.pill,
            backgroundColor: b.background.muted,
          },
          bar: { borderRadius: radius.pill, background: b.gradients.progress },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: b.background.paper,
            color: b.text.primary,
            border: `1px solid ${b.border.light}`,
            boxShadow: shadows.tooltip,
            borderRadius: radius.md,
            fontSize: '0.688rem',
            padding: '8px 12px',
          },
          arrow: { color: b.background.paper },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            fontSize: '0.75rem',
            backgroundColor: white,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: b.border.strong },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: b.border.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: b.primary.main,
              borderWidth: 1,
            },
          },
          input: {
            padding: '10px 14px',
            '&::placeholder': { color: b.text.disabled, opacity: 1 },
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.688rem',
            fontWeight: 600,
            color: b.text.secondary,
            '&.Mui-focused': { color: b.primary.main },
          },
        },
      },

      MuiFormHelperText: {
        styleOverrides: {
          root: { fontSize: '0.625rem', color: b.text.disabled, marginLeft: 2 },
        },
      },

      MuiSelect: {
        defaultProps: { MenuProps: { elevation: 2 } },
        styleOverrides: { select: { padding: '10px 14px' } },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.75rem',
            borderRadius: radius.sm,
            margin: '2px 6px',
            '&.Mui-selected': {
              backgroundColor: b.primary.light,
              color: b.primary.main,
              '&:hover': { backgroundColor: alpha(b.primary.main, 0.12) },
            },
          },
        },
      },

      MuiSwitch: {
        styleOverrides: {
          root: { width: 38, height: 22, padding: 0, display: 'flex' },
          switchBase: {
            padding: 3,
            '&.Mui-checked': {
              transform: 'translateX(16px)',
              color: white,
              '& + .MuiSwitch-track': { opacity: 1, backgroundColor: b.primary.main },
            },
          },
          thumb: {
            width: 16,
            height: 16,
            boxShadow: shadows.chrome,
          },
          track: {
            borderRadius: radius.pill,
            opacity: 1,
            backgroundColor: b.text.secondary,
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 36 },
          indicator: {
            height: 2,
            borderRadius: 1,
            backgroundColor: b.primary.main,
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 36,
            padding: '8px 14px',
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 400,
            color: b.text.secondary,
            '&.Mui-selected': { color: b.primary.main, fontWeight: 500 },
          },
        },
      },

      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            backgroundColor: white,
            borderRadius: radius.lg,
            padding: 5,
            gap: 4,
            border: `1px solid ${b.border.default}`,
            boxShadow: shadows.chrome,
          },
          grouped: {
            border: 'none',
            borderRadius: `${radius.md}px !important`,
          },
        },
      },

      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 400,
            color: b.text.secondary,
            padding: '7px 14px',
            lineHeight: 1,
            '&:hover': { backgroundColor: b.background.muted, color: b.text.primary },
            '&.Mui-selected': {
              color: white,
              backgroundColor: b.primary.main,
              fontWeight: 500,
              '&:hover': { backgroundColor: b.primary.dark },
            },
          },
        },
      },

      MuiDivider: {
        styleOverrides: { root: { borderColor: b.border.default } },
      },

      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'default' },
        styleOverrides: {
          root: {
            backgroundColor: b.background.paper,
            color: b.text.primary,
            boxShadow: shadows.chrome,
            borderBottom: `1px solid ${b.border.default}`,
          },
        },
      },
    },
  });
}

export const theme = createAppTheme();

export default theme;
